import { selectVideoModeCopyTag } from "@/store/ielements-slice-selector";
import { selectLightweightElements } from "@/store/lightweight-elements-selectors";
import { ProjectFilteringSlice } from "@/store/project-filtering/project-filtering-slice";
import { useAppSelector } from "@/store/store-hooks";
import { TreeData } from "@/utils/tree-generation/tree-data";
import {
  filterElement,
  filterSubtree,
  FilterValues,
} from "@/utils/tree-generation/tree-filters-utils";
import { GUID } from "@faro-lotv/foundation";
import { useCallback, useMemo } from "react";
import { useTreeContext } from "../tree-context";

type UseCaptureTreeFilteringParams = {
  /**
   * Whether or not there are any filters active.
   * If `false`, filtering will be skipped.
   */
  hasActiveFilters: boolean;

  /**
   * Text/string to use for the filtering.
   */
  searchText: ProjectFilteringSlice["text"];

  /**
   * The date range to do filtering for.
   */
  filteringDateRange: ProjectFilteringSlice["dateRange"];

  /**
   * The tags to filter for.
   */
  filteringTags: ProjectFilteringSlice["tags"];

  /**
   * The IDs of the tags whose name matches the search text, OR-combined with the name filter.
   */
  textTagIds: GUID[];
};

type NodeFilteringInput = {
  /** The depth level of the node */
  level: number;
  /** The data of the node needed to customize the tree */
  data: TreeData;
  /** The parent node (only the id property is required) */
  parent: { id: GUID } | null;
};

/**
 * @returns a method for filtering the CaptureTree, based on the provided input.
 *    The hook is keeping track of all the elements that have been already filtered in, and any child of such an element
 *    is considered to be filtered in without applying any further filtering.
 *    This allows the tree to show all descendants of a node that is considered to be filtered in.
 *    That set is reset any time one of the filter values change, to make sure that all elements are being re-considered.
 */
export function useCaptureTreeFiltering({
  hasActiveFilters,
  searchText,
  filteringDateRange,
  filteringTags,
  textTagIds,
}: UseCaptureTreeFilteringParams): (node: NodeFilteringInput) => boolean {
  const { tree } = useTreeContext();

  /**
   * Set containing all the IDs of the elements for which it was already decided that they should be rendered.
   * Needs to be re-created every time the filtering changes.
   */
  const elementFilterSet = useMemo(
    () => new Set<GUID>(),
    // Need to re-evaluate all elements again when the search changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [searchText, filteringDateRange, filteringTags, textTagIds, tree],
  );

  const videoModeCopyTagId = useAppSelector(selectVideoModeCopyTag);

  const filterValues = useMemo<FilterValues>(
    () => ({
      text: searchText,
      dateRange: filteringDateRange,
      tags: filteringTags,
      textTagIds,
      videoModeCopyTagId,
    }),
    [
      searchText,
      filteringDateRange,
      filteringTags,
      textTagIds,
      videoModeCopyTagId,
    ],
  );

  const lightweightElements = useAppSelector(selectLightweightElements);

  return useCallback(
    ({ data, parent, level }: NodeFilteringInput) => {
      const { element } = data;

      if (!hasActiveFilters) return true;
      if (!element) return true;

      // Show any descendants of nodes are explicitly filtered in
      // In other words: once a node was hit by a filter, all descendants should be shown
      const isParentShown = !!parent && elementFilterSet.has(parent.id);
      if (level > 0 && isParentShown) {
        elementFilterSet.add(data.id);
        return true;
      }

      if (filterElement(element, lightweightElements, filterValues)) {
        elementFilterSet.add(element.id);
        return true;
      }

      // While the TreeData doesn't provide further children to render, the corresponding project subtree should
      // still be considered for filtering.
      if (!data.children?.length) {
        if (filterSubtree(element, lightweightElements, filterValues)) {
          elementFilterSet.add(data.id);
          return true;
        }
      }

      // No filter hit -> hide element
      return false;
    },
    [hasActiveFilters, elementFilterSet, lightweightElements, filterValues],
  );
}
